// Full-screen settings modal. Discord-style layout, light grape +
// white surface so it lives inside the strawberry/cloud world:
//
//   - Left rail: avatar header, search, category nav (grape tint).
//   - Right pane: white surface with section content for the
//     selected nav item.
//   - Top-right close (X) reuses Dialog's a11y wiring.

import {
  Bot,
  CheckCircle2,
  Database,
  Download,
  Eye,
  Lock,
  LogOut,
  Mic,
  Palette,
  Pencil,
  Search,
  Trash2,
  User,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  Input,
  Label,
  cn,
  useTheme,
} from "@link/ui";

import { StatusDot } from "./StatusDot";
import { useAiSession } from "../state/AiSession";
import { testConnection } from "../state/aiClient";
import {
  PROVIDER_DEFAULT_BASE_URL,
  PROVIDER_LABEL,
  PROVIDER_MODELS,
  type AiProviderConfig,
  type AiProviderId,
} from "../state/aiProvider";
import { clearSessionToken } from "../lib/http";
import { signOutSession } from "../lib/device-session";
import { clearIdentityCache } from "../lib/identity";
import { api } from "../lib/api";
import { useSelf, visibleStatus } from "../state/SelfSession";
import { ProfileEditorSection } from "./ProfileEditorSection";
import { PasskeySettings } from "./auth/PasskeySettings";
import { DeviceSettings } from "./auth/DeviceSettings";
import { VoiceVideoSettings } from "./media/VoiceVideoSettings";
import { DesktopUpdateSettings } from "../state/DesktopUpdates";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type SectionId =
  | "profile"
  | "account"
  | "appearance"
  | "voice-video"
  | "updates"
  | "ai"
  | "data";

const SECTION_IDS: ReadonlyArray<SectionId> = [
  "profile",
  "account",
  "appearance",
  "voice-video",
  "updates",
  "ai",
  "data",
];

function isSectionId(value: string): value is SectionId {
  return (SECTION_IDS as ReadonlyArray<string>).includes(value);
}

const NAV_GROUPS: ReadonlyArray<{
  heading: string;
  items: { id: SectionId; label: string; icon: ReactNode }[];
}> = [
  {
    heading: "User settings",
    items: [
      { id: "account", label: "Account", icon: <User className="h-4 w-4" /> },
      { id: "profile", label: "Profile", icon: <Pencil className="h-4 w-4" /> },
    ],
  },
  {
    heading: "App settings",
    items: [
      { id: "appearance", label: "Appearance", icon: <Palette className="h-4 w-4" /> },
      { id: "voice-video", label: "Voice & video", icon: <Mic className="h-4 w-4" /> },
      { id: "updates", label: "App updates", icon: <Download className="h-4 w-4" /> },
      { id: "ai", label: "AI", icon: <Bot className="h-4 w-4" /> },
      { id: "data", label: "Data & Privacy", icon: <Database className="h-4 w-4" /> },
    ],
  },
];

export function SettingsModal({ open, onOpenChange }: Props) {
  const self = useSelf();
  const [section, setSection] = useState<SectionId>("profile");
  const [filter, setFilter] = useState("");

  // Honor a pending deep-link from any caller of openSettings(...).
  // The provider clears the pending value once we consume it so a
  // future open without a section lands on the default tab.
  useEffect(() => {
    if (!open) return;
    const pending = self.pendingSettingsSection;
    if (pending !== null && isSectionId(pending)) {
      setSection(pending);
      self.consumePendingSettingsSection();
    }
  }, [open, self]);

  const filteredGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) =>
      (item.id !== "updates" || (globalThis as { __LINK_DESKTOP__?: { platform?: string } }).__LINK_DESKTOP__?.platform === "windows")
      && item.label.toLowerCase().includes(filter.toLowerCase()),
    ),
  })).filter((group) => group.items.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="grid h-[100dvh] w-screen max-w-none grid-cols-1 grid-rows-[auto_minmax(0,1fr)] gap-0 overflow-hidden rounded-none border-grape-200 bg-white p-0 text-cloud-700 shadow-2xl shadow-grape-300/30 sm:h-[calc(100dvh-1rem)] sm:w-[calc(100vw-1rem)] sm:rounded-2xl md:h-[88vh] md:max-w-[min(1100px,95vw)] md:grid-cols-[260px_minmax(0,1fr)] md:grid-rows-1">
        <DialogTitle className="sr-only">User settings</DialogTitle>
        <DialogDescription className="sr-only">Manage your profile, appearance, and app preferences.</DialogDescription>
        {/* ---- Left rail ---- */}
        <aside className="liquid-settings-sidebar flex min-h-0 flex-col border-b border-grape-200 bg-grape-50/60 md:border-b-0 md:border-r">
          <SettingsHeader />
          <div className="hidden px-3 py-2 md:block">
            <div className="flex h-9 items-center gap-2 rounded-lg bg-white px-2.5 text-cloud-500 ring-1 ring-grape-200">
              <Search className="h-4 w-4" />
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search"
                className="w-full bg-transparent text-sm outline-none placeholder:text-cloud-400"
              />
            </div>
          </div>
          <nav className="flex gap-2 overflow-x-auto px-3 pb-3 pt-2 md:block md:flex-1 md:overflow-y-auto md:px-2 md:pt-0">
            {filteredGroups.map((group) => (
              <div key={group.heading} className="flex shrink-0 md:mb-3 md:block">
                <p className="hidden px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-cloud-500 md:block">
                  {group.heading}
                </p>
                <ul className="flex gap-1 md:block md:space-y-0.5">
                  {group.items.map((item) => {
                    const active = section === item.id;
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => setSection(item.id)}
                          aria-current={active ? "page" : undefined}
                          className={cn(
                            "liquid-nav-item flex w-auto shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors md:w-full md:gap-3",
                            active
                              ? "bg-white text-cloud-800 shadow-sm ring-1 ring-grape-200"
                              : "text-cloud-700 hover:bg-white/60 hover:text-cloud-800",
                          )}
                        >
                          <span className={cn(active ? "text-strawberry-600" : "text-cloud-500")}>
                            {item.icon}
                          </span>
                          {item.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            <div className="mt-2 hidden border-t border-grape-200 px-2 pt-3 md:block">
              <button
                type="button"
                onClick={() => {
                  void signOutSession().finally(() => {
                    clearIdentityCache();
                    window.location.reload();
                  });
                }}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-cloud-600 hover:bg-white/60 hover:text-red-600"
              >
                <LogOut className="h-4 w-4 text-cloud-500" />
                Log out
              </button>
            </div>
          </nav>
        </aside>

        {/* ---- Right pane ---- */}
        <div className="relative flex min-h-0 flex-col">
          <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 md:px-10 md:py-10">
            {section === "profile" && <ProfileEditorSection />}
            {section === "account" && <AccountSection />}
            {section === "appearance" && <AppearanceSection />}
            {section === "voice-video" && <VoiceVideoSettings />}
            {section === "updates" && <DesktopUpdateSettings />}
            {section === "ai" && <AiSection />}
            {section === "data" && <DataSection />}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
// ---------------------------------------------------------------------------
// Header (avatar + "Edit Profile" lockup at the top of the rail)
// ---------------------------------------------------------------------------

function SettingsHeader() {
  const self = useSelf();
  const dotStatus = visibleStatus(self.status);
  return (
    <div className="flex items-center gap-3 border-b border-grape-200 px-3 py-3">
      <div className="relative">
        <Avatar className="h-10 w-10">
          {self.identity.avatarUrl !== null ? (
            <AvatarImage src={self.identity.avatarUrl} alt="" />
          ) : null}
          <AvatarFallback className="text-sm font-semibold uppercase">
            {self.identity.name.slice(0, 1)}
          </AvatarFallback>
        </Avatar>
        <span className="absolute -bottom-0.5 -right-0.5">
          <StatusDot status={dotStatus} sizeClass="h-2.5 w-2.5" />
        </span>
      </div>
      <div className="min-w-0 flex-1 leading-tight">
        <p className="truncate text-sm font-semibold text-cloud-800">
          {self.identity.name}
        </p>
        <p className="flex items-center gap-1 text-[11px] text-cloud-500">
          <Pencil className="h-3 w-3" />
          Edit profile
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Common helpers
// ---------------------------------------------------------------------------

function SectionHeading({ title, body }: { title: string; body?: string }) {
  return (
    <header className="mb-6 max-w-2xl">
      <h2 className="text-2xl font-semibold text-cloud-800">{title}</h2>
      {body && <p className="mt-1 text-sm text-cloud-500">{body}</p>}
    </header>
  );
}

function FieldGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-5">
      <Label className="text-[11px] font-semibold uppercase tracking-wider text-cloud-500">
        {label}
      </Label>
      {hint && (
        <p className="mt-0.5 text-[11px] text-cloud-400">{hint}</p>
      )}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function LightInput({
  value,
  onChange,
  onBlur,
  placeholder,
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  id?: string;
}) {
  return (
    <Input
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      className="h-10 border-grape-200 bg-white text-cloud-800 placeholder:text-cloud-400"
    />
  );
}

// ---------------------------------------------------------------------------
// Section: Profile
// ---------------------------------------------------------------------------

const PROFILE_BANNER_COLORS = [
  "#ee7f9f",
  "#f3a6b8",
  "#9b7bd3",
  "#6f86d9",
  "#54bfa3",
  "#efaa56",
  "#ca6479",
  "#242733",
] as const;

export function LegacyProfileSection() {
  const self = useSelf();
  const [name, setName] = useState(self.identity.name);
  const [customStatus, setCustomStatus] = useState(
    self.identity.customStatus ?? "",
  );
  const [note, setNote] = useState(self.identity.selfNote);
  const [bannerColor, setBannerColor] = useState(self.identity.bannerColor);
  const avatarInput = useRef<HTMLInputElement>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarMessage, setAvatarMessage] = useState<string | null>(null);

  const dotStatus = visibleStatus(self.status);

  return (
    <div className="max-w-3xl">
      <SectionHeading
        title="Profile"
        body="The look + feel everyone else sees when they hover or click your avatar."
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* Left: editable fields */}
        <div>
          <FieldGroup label="Display name" hint="Public, visible to anyone you DM or share a Space with.">
            <LightInput
              value={name}
              onChange={setName}
              onBlur={() => {
                const trimmed = name.trim();
                if (trimmed.length > 0 && trimmed !== self.identity.name) {
                  self.patchIdentity({ name: trimmed });
                } else {
                  setName(self.identity.name);
                }
              }}
            />
          </FieldGroup>

          <FieldGroup
            label="Custom status"
            hint="Short tagline pinned next to your name. 64 character cap."
          >
            <LightInput
              value={customStatus}
              onChange={setCustomStatus}
              onBlur={() => {
                const trimmed = customStatus.trim();
                self.patchIdentity({
                  customStatus: trimmed.length === 0 ? null : trimmed,
                });
              }}
              placeholder="What are you up to?"
            />
          </FieldGroup>

          <FieldGroup label="Avatar">
            <div className="flex items-center gap-3">
              <input
                ref={avatarInput}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file === undefined) return;
                  setAvatarBusy(true);
                  setAvatarMessage(null);
                  void self.uploadAvatar(file)
                    .then(() => setAvatarMessage("Avatar updated."))
                    .catch((reason: unknown) => setAvatarMessage(reason instanceof Error ? reason.message : "Couldn't upload that avatar."))
                    .finally(() => setAvatarBusy(false));
                }}
              />
              <Button
                variant="default"
                className="bg-strawberry-gradient text-white"
                disabled={avatarBusy}
                onClick={() => avatarInput.current?.click()}
              >
                {avatarBusy ? "Uploading…" : "Change Avatar"}
              </Button>
              <Button
                variant="ghost"
                className="bg-white text-cloud-700 ring-1 ring-grape-200 hover:bg-grape-50"
                disabled={avatarBusy || self.identity.avatarUrl === null}
                onClick={() => {
                  setAvatarBusy(true);
                  setAvatarMessage(null);
                  void self.removeAvatar()
                    .then(() => setAvatarMessage("Avatar removed."))
                    .catch((reason: unknown) => setAvatarMessage(reason instanceof Error ? reason.message : "Couldn't remove your avatar."))
                    .finally(() => setAvatarBusy(false));
                }}
              >
                Remove
              </Button>
            </div>
            <p className="mt-2 text-xs text-cloud-500">PNG, JPEG, WebP, or animated GIF. 8 MB maximum.</p>
            {avatarMessage !== null ? <p className="mt-1 text-xs text-cloud-600">{avatarMessage}</p> : null}
          </FieldGroup>

          <FieldGroup label="Banner color" hint="Pick any color for the banner behind your avatar.">
            <div className="flex flex-wrap items-center gap-2">
              {PROFILE_BANNER_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  aria-label={`Use banner color ${color}`}
                  onClick={() => {
                    setBannerColor(color);
                    self.patchIdentity({ bannerColor: color });
                  }}
                  className="h-9 w-9 rounded-xl border-2 border-white shadow-sm ring-1 ring-cloud-200 transition-transform hover:scale-105"
                  style={{ backgroundColor: color, outline: bannerColor === color ? "2px solid #e6537f" : undefined, outlineOffset: 2 }}
                />
              ))}
              <label className="ml-1 inline-flex h-9 cursor-pointer items-center gap-2 rounded-xl border border-grape-200 bg-white px-3 text-xs font-semibold text-cloud-600">
                Custom
                <input
                  type="color"
                  value={bannerColor}
                  onChange={(event) => {
                    const color = event.target.value.toLowerCase();
                    setBannerColor(color);
                    self.patchIdentity({ bannerColor: color });
                  }}
                  className="h-5 w-7 cursor-pointer border-0 bg-transparent p-0"
                />
              </label>
              <span className="font-mono text-xs uppercase text-cloud-500">{bannerColor}</span>
            </div>
          </FieldGroup>

          <FieldGroup label="Profile note (only visible to you)">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => self.patchIdentity({ selfNote: note })}
              rows={3}
              placeholder="Click to add a note"
              className="w-full resize-none rounded-md border border-grape-200 bg-white px-3 py-2 text-sm text-cloud-700 placeholder:text-cloud-400 focus:outline-none focus:ring-2 focus:ring-strawberry-300"
            />
          </FieldGroup>

        </div>

        {/* Right: live preview card */}
        <div className="lg:sticky lg:top-0">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-cloud-500">
            Preview
          </p>
          <div className="overflow-hidden rounded-2xl border border-grape-200 bg-white shadow-xl shadow-grape-200/40">
            <div className="relative h-20 overflow-hidden">
              <div className="absolute inset-0" style={{ backgroundColor: bannerColor }} />
              <div className="pointer-events-none absolute -left-10 -top-10 h-40 w-40 rounded-full bg-white/30 blur-2xl" />
              <div className="pointer-events-none absolute -right-8 bottom-0 h-24 w-24 rounded-full bg-rose-200/40 blur-xl" />
            </div>
            <div className="-mt-12 px-5 pb-4">
              <div className="relative inline-block rounded-full ring-[6px] ring-white">
                <Avatar className="h-[72px] w-[72px]">
                  {self.identity.avatarUrl !== null ? (
                    <AvatarImage src={self.identity.avatarUrl} alt="" />
                  ) : null}
                  <AvatarFallback className="text-xl font-semibold uppercase">
                    {(name || "?").slice(0, 1)}
                  </AvatarFallback>
                </Avatar>
                <span className="absolute bottom-1 right-1 rounded-full bg-white p-[3px]">
                  <StatusDot
                    status={dotStatus}
                    sizeClass="h-3 w-3"
                    ringClass="ring-white"
                  />
                </span>
              </div>
              <p className="mt-3 truncate text-base font-semibold text-cloud-800">
                {name || "Your name"}
                <span className="ml-1.5 text-sm font-normal text-cloud-500">
                  · @{self.identity.username}
                </span>
              </p>
              {customStatus.trim().length > 0 && (
                <p className="mt-1 truncate text-xs italic text-cloud-600">
                  “{customStatus}”
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Account
// ---------------------------------------------------------------------------

function AccountSection() {
  const self = useSelf();
  const [username, setUsername] = useState(self.identity.username);
  const [usernameBusy, setUsernameBusy] = useState(false);
  const [usernameMessage, setUsernameMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const availableAt = self.identity.usernameChangeAvailableAt === null
    ? null
    : new Date(self.identity.usernameChangeAvailableAt);
  const onCooldown = availableAt !== null && availableAt.getTime() > Date.now();
  return (
    <div className="max-w-3xl">
      <SectionHeading
        title="Account"
        body="Manage your account, saved devices, and ways to get back in."
      />

      <FieldGroup
        label="Username"
        hint={onCooldown && availableAt !== null
          ? `You can change it again on ${availableAt.toLocaleDateString()}.`
          : "Your unique global @username. It can be changed once every 7 days."}
      >
        <div className="flex items-center gap-2">
          <Input
            value={username}
            onChange={(event) => setUsername(event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
            minLength={3}
            maxLength={32}
            disabled={onCooldown || usernameBusy}
            className="h-10 border-grape-200 bg-white text-cloud-700"
          />
          <Button
            disabled={onCooldown || usernameBusy || username === self.identity.username || !/^[a-z0-9_]{3,32}$/.test(username)}
            onClick={() => {
              setUsernameBusy(true);
              setUsernameMessage(null);
              void self.changeUsername(username)
                .then(() => setUsernameMessage("Username updated."))
                .catch((reason: unknown) => {
                  setUsername(self.identity.username);
                  setUsernameMessage(reason instanceof Error ? reason.message : "Couldn't update your username.");
                })
                .finally(() => setUsernameBusy(false));
            }}
          >
            {usernameBusy ? "Saving…" : "Save"}
          </Button>
        </div>
        {usernameMessage !== null ? <p className="mt-2 text-xs text-cloud-600">{usernameMessage}</p> : null}
      </FieldGroup>

      <FieldGroup label="Account ID" hint="Share this with someone who wants to friend you.">
        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={self.identity.accountXid}
            className="h-10 border-grape-200 bg-white font-mono text-cloud-700"
          />
          <Button
            variant="ghost"
            className="bg-white text-cloud-700 ring-1 ring-grape-200 hover:bg-grape-50"
            onClick={() => {
              void navigator.clipboard
                .writeText(self.identity.accountXid)
                .then(() => setCopyMessage("Account ID copied."))
                .catch(() => setCopyMessage("Couldn't copy the Account ID."));
            }}
          >
            Copy
          </Button>
        </div>
        {copyMessage !== null ? (
          <p className="mt-2 text-xs text-cloud-500">{copyMessage}</p>
        ) : null}
      </FieldGroup>

      <FieldGroup label="Member since">
        <p className="text-sm text-cloud-700">
          {new Date(self.identity.memberSince).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </p>
      </FieldGroup>

      <DeviceSettings accountXid={self.identity.accountXid} username={self.identity.username} />
      <details className="mt-6"><summary className="cursor-pointer font-semibold text-cloud-700">Passkeys (optional)</summary><PasskeySettings accountXid={self.identity.accountXid} /></details>

      <div className="mt-10 rounded-2xl border border-red-200 bg-red-50/60 p-5">
        <div className="mb-2 flex items-center gap-2 text-red-600">
          <Trash2 className="h-4 w-4" />
          <p className="text-sm font-semibold">Danger zone</p>
        </div>
        <p className="text-xs text-cloud-600">
          Deleting your account schedules a 7-day grace period. During
          the window your account is invisible to peers and any active
          sessions are ended; cancel any time before the window
          expires.
        </p>
        <div className="mt-3"><AccountDataActions /></div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Appearance
// ---------------------------------------------------------------------------

function AppearanceSection() {
  const { mode, setMode } = useTheme();
  return (
    <div className="max-w-3xl">
      <SectionHeading
        title="Appearance"
        body="Two distinct looks. The same Link. Saved on this device."
      />

      <FieldGroup label="Theme">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ThemeTile
            id="glass"
            label="Rose"
            active={mode === "glass"}
            onSelect={() => setMode("glass")}
            preview={
              <div className="theme-preview-rose flex h-full gap-2 p-4">
                <div className="w-1/4 rounded-xl bg-[#fff2f438] p-2"><div className="h-6 rounded-lg bg-[#9b345a]" /></div>
                <div className="flex flex-1 flex-col gap-2 rounded-xl bg-[#fff2f473] p-3">
                  <div className="h-2 w-3/4 rounded-full bg-[#5c293c99]" />
                  <div className="h-2 w-1/2 rounded-full bg-[#5c293c38]" />
                  <div className="mt-auto h-5 rounded-lg bg-[#fff2f480]" />
                </div>
              </div>
            }
          />
          <ThemeTile
            id="liquid"
            label="Liquid Glass"
            active={mode === "liquid"}
            onSelect={() => setMode("liquid")}
            preview={
              <div className="theme-preview-liquid flex h-full gap-2 p-4">
                <div className="preview-glass w-1/4 rounded-xl p-2"><div className="preview-glass h-6 rounded-full" /></div>
                <div className="preview-glass flex flex-1 flex-col gap-2 rounded-xl p-3">
                  <div className="h-2 w-3/4 rounded-full bg-[#33333380]" />
                  <div className="h-2 w-1/2 rounded-full bg-[#33333330]" />
                  <div className="preview-glass mt-auto h-5 rounded-full" />
                </div>
              </div>
            }
          />
        </div>
      </FieldGroup>

      <p className="mb-3 text-sm leading-relaxed text-cloud-600">
        {mode === "liquid"
          ? "Colorless glass, polished edges, and soft inner shadows. Clear controls, calm surfaces, and crisp dark text."
          : "Deeper rose tones, soft translucent panels, and a rich berry accent."}
      </p>
      <p className="text-xs leading-relaxed text-cloud-500">
        Both themes respect your system’s reduced transparency and increased contrast preferences.
      </p>
    </div>
  );
}

function ThemeTile({
  label,
  active,
  onSelect,
  preview,
}: {
  id: string;
  label: string;
  active: boolean;
  onSelect: () => void;
  preview: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={cn(
        "group flex flex-col overflow-hidden rounded-xl border-2 text-left transition-all",
        active
          ? "border-strawberry-600 shadow-sm"
          : "border-grape-200 hover:border-grape-300",
      )}
    >
      <div className="h-32 w-full overflow-hidden">{preview}</div>
      <div className="flex items-center justify-between bg-white px-3 py-2 text-sm text-cloud-800">
        <span>{label}</span>
        {active && (
          <span className="h-2 w-2 rounded-full bg-strawberry-500" />
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Section: AI
// ---------------------------------------------------------------------------

function AiSection() {
  const ai = useAiSession();

  // Local form state — the user only commits with "Save" so a typo
  // doesn't fan out to the live config mid-key-paste.
  const [providerId, setProviderId] = useState<AiProviderId>(
    ai.config?.providerId ?? "openai",
  );
  const [apiKey, setApiKey] = useState<string>(ai.config?.apiKey ?? "");
  const [model, setModel] = useState<string>(
    ai.config?.model ?? PROVIDER_MODELS.openai[0]!,
  );
  const [baseUrl, setBaseUrl] = useState<string>(
    ai.config?.baseUrl ?? PROVIDER_DEFAULT_BASE_URL[providerId] ?? "",
  );
  const [revealKey, setRevealKey] = useState(false);
  const [testStatus, setTestStatus] = useState<
    | { kind: "idle" }
    | { kind: "running" }
    | { kind: "ok" }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  // When the user picks a different provider, snap the model + base
  // URL to that provider's defaults so they aren't left with stale
  // values (e.g. "claude-3-5-sonnet" on OpenAI).
  const onProviderChange = (next: AiProviderId): void => {
    setProviderId(next);
    setModel(PROVIDER_MODELS[next][0]!);
    setBaseUrl(PROVIDER_DEFAULT_BASE_URL[next] ?? "");
    setTestStatus({ kind: "idle" });
  };

  const buildConfig = (): AiProviderConfig => ({
    providerId,
    apiKey: apiKey.trim(),
    model: model.trim(),
    baseUrl:
      providerId === "ollama"
        ? baseUrl.trim().length > 0
          ? baseUrl.trim()
          : (PROVIDER_DEFAULT_BASE_URL.ollama ?? null)
        : null,
  });

  const onSave = async (): Promise<void> => {
    const config = buildConfig();
    if (config.providerId !== "ollama" && config.apiKey.length === 0) {
      setTestStatus({ kind: "error", message: "Add an API key first." });
      return;
    }
    await ai.save(config);
    setTestStatus({ kind: "ok" });
  };

  const onTest = async (): Promise<void> => {
    const config = buildConfig();
    if (config.providerId !== "ollama" && config.apiKey.length === 0) {
      setTestStatus({ kind: "error", message: "Add an API key first." });
      return;
    }
    setTestStatus({ kind: "running" });
    try {
      await testConnection(config);
      setTestStatus({ kind: "ok" });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Connection failed.";
      setTestStatus({ kind: "error", message });
    }
  };

  const onClear = async (): Promise<void> => {
    await ai.clear();
    setApiKey("");
    setTestStatus({ kind: "idle" });
  };

  return (
    <div className="max-w-3xl">
      <SectionHeading
        title="AI"
        body="Bring your own model. Your prompts go straight from this browser to your chosen provider — the Link server never sees them."
      />

      {/* Privacy banner. */}
      <div className="mb-6 flex items-start gap-3 rounded-xl border border-strawberry-200 bg-strawberry-50/70 p-3">
        <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-strawberry-600" />
        <p className="text-xs leading-relaxed text-cloud-700">
          Your API key lives in this browser's IndexedDB next to your
          Identity_Key. It never crosses the Link backend. Logging
          out from this device clears it.
        </p>
      </div>

      <FieldGroup label="Provider">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {(Object.keys(PROVIDER_LABEL) as AiProviderId[]).map((id) => {
            const active = providerId === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => onProviderChange(id)}
                className={cn(
                  "rounded-xl border-2 p-3 text-left transition-colors",
                  active
                    ? "border-strawberry-400 bg-strawberry-50/60"
                    : "border-grape-200 bg-white hover:border-grape-300",
                )}
              >
                <p className="text-sm font-semibold text-cloud-800">
                  {PROVIDER_LABEL[id]}
                </p>
                <p className="mt-0.5 text-[11px] text-cloud-500">
                  {id === "openai" && "GPT-4o, GPT-4.1, etc."}
                  {id === "anthropic" && "Claude 3.5, Opus 4.x"}
                  {id === "ollama" && "Local runner on your machine"}
                </p>
              </button>
            );
          })}
        </div>
      </FieldGroup>

      <FieldGroup
        label="API key"
        hint={
          providerId === "ollama"
            ? "Optional for Ollama; required only if your runner is auth-gated."
            : "Stored locally. We can't read it; the Link server can't either."
        }
      >
        <div className="flex items-center gap-2">
          <Input
            type={revealKey ? "text" : "password"}
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={
              providerId === "openai"
                ? "sk-..."
                : providerId === "anthropic"
                  ? "sk-ant-..."
                  : "(optional)"
            }
            className="h-10 flex-1 border-grape-200 bg-white font-mono text-cloud-800 placeholder:text-cloud-400"
          />
          <Button
            variant="ghost"
            type="button"
            onClick={() => setRevealKey((v) => !v)}
            className="bg-white text-cloud-700 ring-1 ring-grape-200 hover:bg-grape-50"
          >
            <Eye className="h-4 w-4" />
            {revealKey ? "Hide" : "Show"}
          </Button>
        </div>
      </FieldGroup>

      <FieldGroup label="Model">
        <div className="flex items-center gap-2">
          <Input
            list={`models-${providerId}`}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="h-10 flex-1 border-grape-200 bg-white text-cloud-800"
          />
          <datalist id={`models-${providerId}`}>
            {PROVIDER_MODELS[providerId].map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
        </div>
      </FieldGroup>

      {providerId === "ollama" && (
        <FieldGroup
          label="Base URL"
          hint="Where your OpenAI-compatible server lives. Default is Ollama at localhost."
        >
          <Input
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="http://localhost:11434/v1"
            className="h-10 border-grape-200 bg-white text-cloud-800 placeholder:text-cloud-400"
          />
        </FieldGroup>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <Button onClick={onSave} className="bg-strawberry-gradient text-white">
          Save
        </Button>
        <Button
          variant="ghost"
          onClick={onTest}
          disabled={testStatus.kind === "running"}
          className="bg-white text-cloud-700 ring-1 ring-grape-200 hover:bg-grape-50"
        >
          {testStatus.kind === "running" ? "Testing…" : "Test connection"}
        </Button>
        {ai.config !== null && (
          <Button
            variant="ghost"
            onClick={onClear}
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
            Forget key
          </Button>
        )}
      </div>

      {testStatus.kind === "ok" && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 ring-1 ring-emerald-200">
          <CheckCircle2 className="h-4 w-4" />
          Connection looks good.
        </div>
      )}
      {testStatus.kind === "error" && (
        <div className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          {testStatus.message}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Data & Privacy
// ---------------------------------------------------------------------------

function DataSection() {
  return (
    <div className="max-w-3xl">
      <SectionHeading
        title="Data & Privacy"
        body="What's stored on your device, what's stored on the server, and how to take it with you."
      />

      <FieldGroup label="On this device" hint="Your private key is non-extractable; we can't read it and neither can anyone else.">
        <div className="rounded-lg border border-grape-200 bg-grape-50/40 p-3 text-sm text-cloud-700">
          <Eye className="mb-2 h-4 w-4 text-cloud-500" />
          Identity_Key is held in IndexedDB on this browser. Logging
          out forgets it.
        </div>
      </FieldGroup>

      <FieldGroup label="Account controls" hint="Exports are prepared securely. Account deletion has a 7-day grace period.">
        <AccountDataActions />
      </FieldGroup>
    </div>
  );
}

function AccountDataActions() {
  const self = useSelf();
  const [busy, setBusy] = useState<"export" | "delete" | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const requestExport = async (): Promise<void> => {
    setBusy("export");
    setMessage(null);
    try {
      await api.me.exportRequest();
      setMessage("Your export is being prepared.");
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "Couldn't request an export.");
    } finally {
      setBusy(null);
    }
  };

  const scheduleDeletion = async (): Promise<void> => {
    const confirmed = window.confirm(
      "Schedule this account for deletion? You will be signed out immediately and have 7 days to cancel.",
    );
    if (!confirmed) return;
    setBusy("delete");
    setMessage(null);
    try {
      await api.me.scheduleDeletion({ account_xid: self.identity.accountXid });
      clearSessionToken();
      clearIdentityCache();
      window.location.reload();
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "Couldn't schedule account deletion.");
      setBusy(null);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          disabled={busy !== null}
          onClick={() => void requestExport()}
        >
          {busy === "export" ? "Requesting…" : "Export my data"}
        </Button>
        <Button
          variant="destructive"
          disabled={busy !== null}
          onClick={() => void scheduleDeletion()}
        >
          {busy === "delete" ? "Scheduling…" : "Schedule deletion"}
        </Button>
      </div>
      {message !== null ? (
        <p className="mt-2 text-xs text-cloud-500">{message}</p>
      ) : null}
    </div>
  );
}
